import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { useLocale } from '../i18n/LocaleContext';
import { formatPrice } from '../billing/catalog';

const PaymentModal = ({ isOpen, onClose, prices, selectedPrice, onPeriodChange, onSubmit }) => {
  const { t, locale } = useLocale();
  const closeButton = useRef(null);
  const dialog = useRef(null);
  useEffect(() => {
    if (!isOpen) return undefined;
    const previous = document.activeElement;
    closeButton.current?.focus();
    const handleKey = event => {
      if (event.key === 'Escape') onClose();
      if (event.key === 'Tab') {
        const items = dialog.current?.querySelectorAll('button:not([disabled]), input:not([disabled]), a[href]');
        if (!items?.length) return;
        const first = items[0]; const last = items[items.length - 1];
        if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
        else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => { document.removeEventListener('keydown', handleKey); previous?.focus(); };
  }, [isOpen, onClose]);
  if (!isOpen || !selectedPrice) return null;
  const monthly = prices.find(price => price.interval === 'monthly');
  const savings = monthly && selectedPrice.interval === 'yearly' ? monthly.unitAmount * 12 - selectedPrice.unitAmount : 0;
  return (
    <PaymentModalOverlay>
      <PaymentModalContent ref={dialog} role="dialog" aria-modal="true" aria-labelledby="payment-title">
        <ModalHeader id="payment-title">{t('payment.title')}</ModalHeader>
        <PriceSummary>
          <PriceDisplay><PriceAmount>{formatPrice(selectedPrice, locale)}</PriceAmount>{t(selectedPrice.interval === 'yearly' ? 'pricing.perYear' : 'pricing.perMonth')}</PriceDisplay>
          {selectedPrice.taxBehavior === 'inclusive' && <p>{t('pricing.vatIncluded')}</p>}
          {selectedPrice.taxBehavior === 'exclusive' && <p>{t('pricing.vatAdditional')}</p>}
          {savings > 0 && <SavingsNotice>{t('payment.savings', { amount: formatPrice({ ...selectedPrice, unitAmount: savings }, locale) })}</SavingsNotice>}
        </PriceSummary>
        <PaymentForm onSubmit={onSubmit}>
          <BillingOptions>{prices.map(price => <BillingOption key={price.interval} $selected={selectedPrice.interval === price.interval}>
            <input type="radio" name="paymentPeriod" checked={selectedPrice.interval === price.interval} disabled={!price.available} onChange={() => onPeriodChange(price.interval)} />
            <span>{t(price.interval === 'yearly' ? 'payment.yearly' : 'payment.monthly')}</span>
          </BillingOption>)}</BillingOptions>
          <ModalInfo><p>{t('payment.handoff')}</p></ModalInfo>
          <ModalActions>
            <SubmitButton type="submit" disabled={!selectedPrice.available}>{t('payment.continue')}</SubmitButton>
            <CancelButton ref={closeButton} type="button" onClick={onClose}>{t('common.cancel')}</CancelButton>
          </ModalActions>
        </PaymentForm>
      </PaymentModalContent>
    </PaymentModalOverlay>
  );
};

// Styled Components
const PaymentModalOverlay = styled.div`
  position: fixed;
  top: 0;
  inset-inline-start: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const PaymentModalContent = styled.div`
  background-color: #ffffff;
  border-radius: 16px;
  width: 100%;
  max-width: 550px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  padding: 32px;

  @media (max-width: 600px) {
    padding: 24px;
    width: 90%;
  }
`;

const ModalHeader = styled.h2`
  font-size: 1.8rem;
  color: var(--primary-dark);
  margin-bottom: 20px;
  text-align: center;
  font-weight: 700;
`;

const PriceSummary = styled.div`
  background-color: #f2f7f7;
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  text-align: center;
`;

const PriceDisplay = styled.p`
  font-size: 1.2rem;
  color: var(--secondary);
  margin-bottom: 8px;
`;

const PriceAmount = styled.span`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary);
`;

const SavingsNotice = styled.p`
  color: #16a34a;
  font-weight: 600;
  margin-top: 8px;
`;

const PaymentForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const BillingOptions = styled.div`
  display: flex;
  justify-content: space-between;
  margin-bottom: 10px;
  gap: 15px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const BillingOption = styled.label`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 12px 16px;
  background-color: ${props => props.$selected ? '#f0f9f9' : '#f9fafb'};
  border: 2px solid ${props => props.$selected ? 'var(--primary)' : '#e5e7eb'};
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #f2f7f7;
  }

  span {
    color: ${props => props.$selected ? 'var(--primary)' : 'var(--secondary)'};
    font-weight: ${props => props.$selected ? '600' : '400'};
  }
`;

const ModalActions = styled.div`
  display: flex;
  gap: 15px;
  margin-top: 10px;

  @media (max-width: 600px) {
    flex-direction: column;
  }
`;

const SubmitButton = styled.button`
  flex: 2;
  background: var(--gradient);
  color: white;
  padding: 14px 20px;
  border: none;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(0, 128, 128, 0.15);
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const CancelButton = styled.button`
  flex: 1;
  background-color: #f3f4f6;
  color: var(--secondary);
  padding: 14px 20px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background-color: #e5e7eb;
  }

  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  @media (max-width: 600px) {
    width: 100%;
  }
`;

const ModalInfo = styled.div`
  margin-top: 25px;
  padding-top: 20px;
  border-top: 1px solid #e5e7eb;
  text-align: center;
  color: var(--secondary);
  font-size: 0.9rem;

  p {
    margin: 5px 0;
  }

  strong {
    color: var(--primary);
  }
`;

export default PaymentModal;
